import { DateInput } from '../../../components/DateInput/DateInput';
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { investorService } from '../../../services/investor.service';
import { contentService } from '../../../services/content.service';
import type {
  IndividualRegistrationDto,
  LegalEntityRegistrationDto
} from '../../../services/investor.service';
import type {
  MasterCountryDto,
  MasterLookupDto,
  MasterTitleDto
} from '../../../services/content.service';
import TermsModal from '../IntroducedRegistration/TermsModal';
import { toast } from 'react-toastify';
import { PremiumSelect } from '../../../components/PremiumSelect/PremiumSelect';
import '../IntroducedRegistration/IntroducedInvestorRegistration.scss';
import { CompactHeader } from '../../../components/CompactHeader/CompactHeader';
import { CompactFooter } from '../../../components/CompactFooter/CompactFooter';
import { RegistrationStepper } from '../../../components/RegistrationStepper/RegistrationStepper';
import { RegistrationVisualCard } from '../../../components/RegistrationVisualCard/RegistrationVisualCard';
import { SELF_STEPS } from './selfSteps';


/**
 * Step 3: Registration Details with consent confirmations
 * Matches Laravel: register-step4.blade.php
 */
export const SelfRegistrationStep3: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { uniqueCode, email, registerAs, consentGiven } = location.state || {};

  const [loading, setLoading] = useState(false);
  const [countries, setCountries] = useState<MasterCountryDto[]>([]);
  const [nationalities, setNationalities] = useState<MasterLookupDto[]>([]);
  const [titles, setTitles] = useState<MasterTitleDto[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Modal states
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [termsCheckboxEnabled, setTermsCheckboxEnabled] = useState(false);

  // Additional checkboxes
  const [informationAccuracy, setInformationAccuracy] = useState(false);
  const [showErrors, setShowErrors] = useState(false);

  // Individual form data
  const [individualData, setIndividualData] = useState<Partial<IndividualRegistrationDto>>({
    interestedInIndianMarket: true, // Already confirmed in Market Interest Check (Step 1)
    title: undefined,
    firstName: '',
    middleName: '',
    lastName: '',
    dateOfBirth: '',
    gender: '',
    nationality: 0,
    countryOfResidence: 0,
    residencyType: '',
    termsAccepted: false,
  });

  // Legal Entity form data
  const [legalEntityData, setLegalEntityData] = useState<Partial<LegalEntityRegistrationDto>>({
    entityName: '',
    countryOfIncorporation: 0,
    hasPanCard: undefined,
    entityRepresentativeName: '',
    representativeCapacity: '',
    isSecuritiesRegulated: undefined,
    termsAccepted: false,
  });

  useEffect(() => {
    if (!uniqueCode || !email) {
      navigate('/investor/register');
      return;
    }

    contentService.getCountries().then(setCountries).catch(() => { });
    contentService.getNationalities().then(setNationalities).catch(() => { });
    contentService.getTitles().then(setTitles).catch(() => { });
  }, [uniqueCode, email, navigate]);

  const openTermsModal = (e?: React.MouseEvent) => {
    e?.preventDefault();
    e?.stopPropagation();
    setShowTermsModal(true);
  };

  const closeTermsModal = () => {
    setShowTermsModal(false);
  };

  const acceptTerms = () => {
    setTermsCheckboxEnabled(true);
    setTermsAccepted(true);
    setShowTermsModal(false);
  };

  const handleTermsCheckboxClick = (e: React.MouseEvent) => {
    if (!termsAccepted) {
      e.preventDefault();
      openTermsModal(e);
    }
  };

  const handleIndividualSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});
    setShowErrors(true);

    // Validate checkboxes first
    if (!informationAccuracy || !termsAccepted) {
      toast.error('Please accept all consents and terms to continue.');
      return;
    }

    // Validate form fields
    const validationErrors: Record<string, string> = {};
    if (!individualData.title) {
      validationErrors.title = 'Title is required';
    }
    if (!individualData.firstName?.trim()) {
      validationErrors.firstName = 'First name is required';
    }
    if (!individualData.lastName?.trim()) {
      validationErrors.lastName = 'Last name is required';
    }
    if (!individualData.dateOfBirth) {
      validationErrors.dateOfBirth = 'Date of birth is required';
    }
    if (!individualData.gender) {
      validationErrors.gender = 'Gender is required';
    }
    if (!individualData.nationality || individualData.nationality === 0) {
      validationErrors.nationality = 'Nationality is required';
    }
    if (!individualData.countryOfResidence || individualData.countryOfResidence === 0) {
      validationErrors.countryOfResidence = 'Country of residence is required';
    }
    if (needsOciAnswer && individualData.hasOciCard === undefined) {
      validationErrors.hasOciCard = 'Please tell us whether you have an OCI card';
    }

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      toast.error('Please correct the errors in the form');
      return;
    }

    setLoading(true);
    try {
      const response = await investorService.completeIndividualRegistration(uniqueCode, {
        ...individualData,
        // Asked of non-Indian nationals only; never sent for an Indian national.
        hasOciCard: needsOciAnswer ? individualData.hasOciCard : undefined,
        termsAccepted: true,
      } as IndividualRegistrationDto);

      if (!response.success) {
        toast.error(response.message || 'Registration failed');
        return;
      }

      // No OCI card: the server closed the registration without an account.
      if (response.ociCardRequired) {
        navigate('/investor/register/oci-required', { state: { email } });
        return;
      }

      toast.success(response.message);
      navigate('/investor/register/success', { state: { azureUserId: response.azureUserId } });
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  const handleLegalEntitySubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});
    setShowErrors(true);

    // Validate checkboxes first
    if (!informationAccuracy || !termsAccepted) {
      toast.error('Please accept all consents and terms to continue.');
      return;
    }

    // Validate form fields
    const validationErrors: Record<string, string> = {};
    if (!legalEntityData.entityName?.trim()) {
      validationErrors.entityName = 'Entity name is required';
    }
    if (!legalEntityData.countryOfIncorporation || legalEntityData.countryOfIncorporation === 0) {
      validationErrors.countryOfIncorporation = 'Country of incorporation is required';
    }
    if (!legalEntityData.entityRepresentativeName?.trim()) {
      validationErrors.entityRepresentativeName = 'Representative name is required';
    }
    if (!legalEntityData.representativeCapacity?.trim()) {
      validationErrors.representativeCapacity = 'Representative capacity is required';
    }
    if (legalEntityData.isSecuritiesRegulated === undefined) {
      validationErrors.isSecuritiesRegulated = 'Please indicate if entity is regulated';
    }

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      toast.error('Please correct the errors in the form');
      return;
    }

    setLoading(true);
    try {
      const response = await investorService.completeLegalEntityRegistration(uniqueCode, {
        ...legalEntityData,
        termsAccepted: true,
      } as LegalEntityRegistrationDto);

      if (!response.success) {
        toast.error(response.message || 'Registration failed');
        return;
      }

      toast.success(response.message);
      navigate('/investor/register/success', { state: { azureUserId: response.azureUserId } });
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  // Same test as the server's isIndiaText: ISO code "IN" or the name India / Indian.
  const selectedNationality = nationalities.find(n => n.id === individualData.nationality);
  const isIndianNationality = !!selectedNationality && (
    (selectedNationality.externalId || '').trim().toUpperCase() === 'IN'
    || ['india', 'indian'].includes((selectedNationality.name || '').trim().toLowerCase()));
  const needsOciAnswer = !!selectedNationality && !isIndianNationality;

  const isIndiaResidence = () => {
    const indiaCountry = countries.find(c => c.ssName === 'India');
    return individualData.countryOfResidence === indiaCountry?.id;
  };

  const isIndiaIncorporation = () => {
    const indiaCountry = countries.find(c => c.ssName === 'India');
    return legalEntityData.countryOfIncorporation === indiaCountry?.id;
  };

  return (
    <>
      <CompactHeader />

      <section className="login-form-style4 steps4-sec section-padding align-items-center">
        <div className="container">
          <div className="registration-split-layout">
            {/* Left Panel: Form & Stepper */}
            <div className="left-panel">
              {/* Welcome text */}
              {/* <div className="lgf4_Left_content mb-3" style={{ width: '100%', maxWidth: '850px' }}>
                <h3 className="text-center text-lg-start m-0" style={{ lineHeight: '1.4' }}>
                  Welcome to <br />
                  <span>Facilon Services</span>
                </h3>
              </div> */}

              {/* Stepper Progress */}
              <RegistrationStepper currentStep={4} title="Registration Details" maxWidth="850px" steps={SELF_STEPS} />

              {/* Investor Registration Card */}
              <div className="login-form-style3-main" style={{ width: '100%', maxWidth: '850px', margin: '0 auto', textAlign: 'left' }}>
                <div className="login-form-style3-main_full">
                  <div className="login-register3-form-middle">
                    {registerAs === 1 ? (
                      /* INDIVIDUAL REGISTRATION FORM */
                      <form onSubmit={handleIndividualSubmit}>
                        {/* <h3>Individual Registration</h3> */}

                        {/* Title */}
                        <div className="single-field">
                          <label htmlFor="title">Title <span className="star-color">*</span></label>
                          <PremiumSelect
                            value={individualData.title?.toString() ?? ''}
                            onChange={(val) => {
                              setIndividualData((p) => ({
                                ...p,
                                title: val === '' ? undefined : Number.parseInt(val, 10),
                              }));
                            }}
                            options={titles.map((t) => ({
                              value: String(t.id),
                              label: t.ssName || ''
                            }))}
                            placeholder="Select Title"
                          />
                          {errors.title && <span role="alert">{errors.title}</span>}
                        </div>

                        {/* First Name */}
                        <div className="single-field">
                          <label htmlFor="firstName">First Name <span className="star-color">*</span></label>
                          <input
                            type="text"
                            id="firstName"
                            className="form-control"
                            value={individualData.firstName}
                            onChange={(e) => setIndividualData(p => ({ ...p, firstName: e.target.value.toUpperCase() }))}
                            placeholder="Enter your first name"
                            style={{ textTransform: 'uppercase' }}
                          />
                          {errors.firstName && <span role="alert">{errors.firstName}</span>}
                        </div>

                        {/* Middle Name */}
                        <div className="single-field">
                          <label htmlFor="middleName">Middle Name</label>
                          <input
                            type="text"
                            id="middleName"
                            className="form-control"
                            value={individualData.middleName}
                            onChange={(e) => setIndividualData(p => ({ ...p, middleName: e.target.value.toUpperCase() }))}
                            placeholder="Enter your middle name (optional)"
                            style={{ textTransform: 'uppercase' }}
                          />
                        </div>

                        {/* Last Name */}
                        <div className="single-field">
                          <label htmlFor="lastName">Last Name <span className="star-color">*</span></label>
                          <input
                            type="text"
                            id="lastName"
                            className="form-control"
                            value={individualData.lastName}
                            onChange={(e) => setIndividualData(p => ({ ...p, lastName: e.target.value.toUpperCase() }))}
                            placeholder="Enter your last name"
                            style={{ textTransform: 'uppercase' }}
                          />
                          {errors.lastName && <span role="alert">{errors.lastName}</span>}
                        </div>

                        {/* Date of Birth */}
                        <div className="single-field">
                          <label htmlFor="dateOfBirth">Date of Birth <span className="star-color">*</span></label>
                          <DateInput
                            id="dateOfBirth"
                            className="form-control"
                            value={individualData.dateOfBirth}
                            onChange={(value) => setIndividualData(p => ({ ...p, dateOfBirth: value }))}
                          />
                          {errors.dateOfBirth && <span role="alert">{errors.dateOfBirth}</span>}
                        </div>

                        {/* Gender */}
                        <div className="single-field self-sec">
                          <label>Gender <span className="star-color">*</span></label>
                          <div className="radio-box">
                            <label className="radio">
                              <input
                                type="radio"
                                name="gender"
                                value="MALE"
                                checked={individualData.gender === 'MALE'}
                                onChange={(e) => setIndividualData(p => ({ ...p, gender: e.target.value }))}
                              />
                              <span>Male</span>
                            </label>
                            <label className="radio">
                              <input
                                type="radio"
                                name="gender"
                                value="FEMALE"
                                checked={individualData.gender === 'FEMALE'}
                                onChange={(e) => setIndividualData(p => ({ ...p, gender: e.target.value }))}
                              />
                              <span>Female</span>
                            </label>
                            <label className="radio">
                              <input
                                type="radio"
                                name="gender"
                                value="TRANSGENDER"
                                checked={individualData.gender === 'TRANSGENDER'}
                                onChange={(e) => setIndividualData(p => ({ ...p, gender: e.target.value }))}
                              />
                              <span>Transgender</span>
                            </label>
                          </div>
                          {errors.gender && <span role="alert">{errors.gender}</span>}
                        </div>

                        {/* Nationality */}
                        <div className="single-field">
                          <label htmlFor="nationality">Nationality <span className="star-color">*</span></label>
                          <PremiumSelect
                            value={individualData.nationality?.toString() ?? '0'}
                            onChange={(val) => setIndividualData(p => ({ ...p, nationality: parseInt(val), hasOciCard: undefined }))}
                            options={nationalities.map(n => ({
                              value: String(n.id),
                              label: n.name || ''
                            }))}
                            placeholder="Select Nationality"
                            searchable
                          />
                          {errors.nationality && <span role="alert">{errors.nationality}</span>}
                        </div>

                        {/* Country of Residence */}
                        <div className="single-field">
                          <label htmlFor="countryOfResidence">Country of Residence <span className="star-color">*</span></label>
                          <PremiumSelect
                            value={individualData.countryOfResidence?.toString() ?? '0'}
                            onChange={(val) => setIndividualData(p => ({ ...p, countryOfResidence: parseInt(val) }))}
                            options={countries.map(c => ({
                              value: String(c.id),
                              label: c.ssName || ''
                            }))}
                            placeholder="Select Country"
                            searchable
                          />
                          {errors.countryOfResidence && <span role="alert">{errors.countryOfResidence}</span>}
                        </div>

                        {/* No PAN or Indian-origin questions (removed at the client's request): the investor type comes
                            from nationality and residence alone. The OCI-card question is back for non-Indian nationals
                            only (user 2026-10-12): they register as OCI, whose OCI card is a mandatory KYC document, so
                            "No" ends the registration with no account instead of a journey they cannot finish. */}
                        {needsOciAnswer && (
                          <div className="single-field self-sec">
                            <label>Do you have an OCI (Overseas Citizen of India) card? <span className="star-color">*</span></label>
                            <div className="radio-box">
                              <label className="radio">
                                <input
                                  type="radio"
                                  name="hasOciCard"
                                  value="yes"
                                  checked={individualData.hasOciCard === true}
                                  onChange={() => setIndividualData(p => ({ ...p, hasOciCard: true }))}
                                />
                                <span>Yes</span>
                              </label>
                              <label className="radio">
                                <input
                                  type="radio"
                                  name="hasOciCard"
                                  value="no"
                                  checked={individualData.hasOciCard === false}
                                  onChange={() => setIndividualData(p => ({ ...p, hasOciCard: false }))}
                                />
                                <span>No</span>
                              </label>
                            </div>
                            {errors.hasOciCard && <span role="alert">{errors.hasOciCard}</span>}
                          </div>
                        )}

                        {/* Consents */}
                        <div className="consent-container m-0 p-0">
                          <div id="note_confirm_div" style={{ marginTop: '24px' }}>
                            {/* 1. Information Accuracy */}
                            <div
                              className={`highlight ${informationAccuracy ? 'checked' : ''}`}
                              style={{
                                backgroundColor: showErrors && !informationAccuracy ? '#ffebee' : undefined,
                                borderLeft: showErrors && !informationAccuracy ? '4px solid #be1717' : undefined
                              }}
                            >
                              <label htmlFor="informationAccuracy">
                                <input
                                  type="checkbox"
                                  id="informationAccuracy"
                                  checked={informationAccuracy}
                                  className="w-4 h-4 text-[#be1717] bg-white border-slate-300 rounded focus:ring-[#be1717] cursor-pointer align-middle mr-2"
                                  onChange={(e) => setInformationAccuracy(e.target.checked)}
                                />
                                <span>
                                  I hereby confirm that the information provided is accurate, correct and complete <span className="star-color">*</span>
                                </span>
                              </label>
                            </div>
                            {showErrors && !informationAccuracy && (
                              <div style={{ color: '#be1717', fontSize: '11.5px', marginTop: '-8px', marginBottom: '12px', paddingLeft: '4px', fontWeight: '500' }}>
                                <i className="bi bi-exclamation-circle-fill me-1"></i> Please confirm that the information provided is accurate.
                              </div>
                            )}

                            {/* 2. Terms and Conditions */}
                            <div
                              className={`highlight ${termsAccepted ? 'checked' : ''}`}
                              onClick={handleTermsCheckboxClick}
                              style={{
                                backgroundColor: showErrors && !termsAccepted ? '#ffebee' : undefined,
                                borderLeft: showErrors && !termsAccepted ? '4px solid #be1717' : undefined
                              }}
                            >
                              <label htmlFor="termsCheckbox">
                                <input
                                  type="checkbox"
                                  id="termsCheckbox"
                                  className="w-4 h-4 text-[#be1717] bg-white border-slate-300 rounded focus:ring-[#be1717] cursor-pointer align-middle mr-2"
                                  checked={termsAccepted}
                                  readOnly={!termsAccepted}
                                  onChange={(e) => {
                                    if (termsAccepted) {
                                      setTermsAccepted(e.target.checked);
                                    }
                                  }}
                                />
                                <span>
                                  I have read, understood and hereby accept the{' '}
                                  <a
                                    href="javascript:void(0);"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      openTermsModal();
                                    }}
                                  >
                                    Terms and Conditions
                                  </a>{' '}
                                  <span className="star-color">*</span>
                                </span>
                              </label>
                            </div>
                            {showErrors && !termsAccepted && (
                              <div style={{ color: '#be1717', fontSize: '11.5px', marginTop: '-8px', marginBottom: '12px', paddingLeft: '4px', fontWeight: '500' }}>
                                <i className="bi bi-exclamation-circle-fill me-1"></i> Please accept the Terms and Conditions to continue.
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Submit Button */}
                        <div id="button_div" style={{ marginTop: '20px' }}>

                          <div className="single-field mb-0 border-t pt-3 mt-3 flex justify-center gap-2">
                            <button
                              className="btn btn-primary"
                              type="submit"
                              disabled={loading}
                            >
                              {loading ? 'Submitting...' : 'Submit'}
                            </button>
                            <button
                              type="button"
                              className="btn btn-outline-primary"
                              onClick={() => navigate('/investor/register/email', { state: { email, registerAs, consentGiven } })}
                            >
                              Back
                            </button>
                          </div>
                        </div>
                      </form>
                    ) : (
                      /* LEGAL ENTITY REGISTRATION FORM */
                      <form onSubmit={handleLegalEntitySubmit}>
                        <h3>Legal Entity Registration</h3>

                        {/* Entity Name */}
                        <div className="single-field">
                          <label htmlFor="entityName">Legal Name of the Entity <span className="star-color">*</span></label>
                          <input
                            type="text"
                            id="entityName"
                            className="form-control"
                            value={legalEntityData.entityName}
                            onChange={(e) => setLegalEntityData(p => ({ ...p, entityName: e.target.value }))}
                            placeholder="Enter entity name"
                          />
                          {errors.entityName && <span role="alert">{errors.entityName}</span>}
                        </div>

                        {/* Country of Incorporation */}
                        <div className="single-field">
                          <label htmlFor="countryOfIncorporation">Country of Incorporation <span className="star-color">*</span></label>
                          <PremiumSelect
                            value={legalEntityData.countryOfIncorporation?.toString() ?? '0'}
                            onChange={(val) => setLegalEntityData(p => ({ ...p, countryOfIncorporation: parseInt(val) }))}
                            options={countries.map(c => ({
                              value: String(c.id),
                              label: c.ssName || ''
                            }))}
                            placeholder="Select Country"
                            searchable
                          />
                          {errors.countryOfIncorporation && <span role="alert">{errors.countryOfIncorporation}</span>}
                        </div>

                        {/* PAN Card for India */}
                        {isIndiaIncorporation() && (
                          <div className="single-field self-sec">
                            <label>Does entity have PAN Card? <span className="star-color">*</span></label>
                            <div className="radio-box">
                              <label className="radio">
                                <input
                                  type="radio"
                                  name="hasPanCard"
                                  checked={legalEntityData.hasPanCard === true}
                                  onChange={() => setLegalEntityData(p => ({ ...p, hasPanCard: true }))}
                                />
                                <span>Yes</span>
                              </label>
                              <label className="radio">
                                <input
                                  type="radio"
                                  name="hasPanCard"
                                  checked={legalEntityData.hasPanCard === false}
                                  onChange={() => setLegalEntityData(p => ({ ...p, hasPanCard: false }))}
                                />
                                <span>No</span>
                              </label>
                            </div>
                          </div>
                        )}

                        {/* Representative Name */}
                        <div className="single-field">
                          <label htmlFor="entityRepresentativeName">Name of the Entity Representative <span className="star-color">*</span></label>
                          <input
                            type="text"
                            id="entityRepresentativeName"
                            className="form-control"
                            value={legalEntityData.entityRepresentativeName}
                            onChange={(e) => setLegalEntityData(p => ({ ...p, entityRepresentativeName: e.target.value }))}
                            placeholder="Name should be same as in the identity document"
                          />
                          {errors.entityRepresentativeName && <span role="alert">{errors.entityRepresentativeName}</span>}
                        </div>

                        {/* Representative Capacity */}
                        <div className="single-field self-sec">
                          <label>In what capacity representing company <span className="star-color">*</span></label>
                          <div className="radio-box">
                            <label className="radio">
                              <input
                                type="radio"
                                name="representativeCapacity"
                                value="Director"
                                checked={legalEntityData.representativeCapacity === 'Director'}
                                onChange={(e) => setLegalEntityData(p => ({ ...p, representativeCapacity: e.target.value }))}
                              />
                              <span>Director</span>
                            </label>
                            <label className="radio">
                              <input
                                type="radio"
                                name="representativeCapacity"
                                value="Employee"
                                checked={legalEntityData.representativeCapacity === 'Employee'}
                                onChange={(e) => setLegalEntityData(p => ({ ...p, representativeCapacity: e.target.value }))}
                              />
                              <span>Employee</span>
                            </label>
                            <label className="radio">
                              <input
                                type="radio"
                                name="representativeCapacity"
                                value="POA Holder"
                                checked={legalEntityData.representativeCapacity === 'POA Holder'}
                                onChange={(e) => setLegalEntityData(p => ({ ...p, representativeCapacity: e.target.value }))}
                              />
                              <span>POA Holder</span>
                            </label>
                          </div>
                          {errors.representativeCapacity && <span role="alert">{errors.representativeCapacity}</span>}
                        </div>

                        {/* Securities Regulated */}
                        <div className="single-field self-sec">
                          <label>Whether Entity is regulated as a Securities or Banking Company <span className="star-color">*</span></label>
                          <div className="radio-box">
                            <label className="radio">
                              <input
                                type="radio"
                                name="isSecuritiesRegulated"
                                checked={legalEntityData.isSecuritiesRegulated === true}
                                onChange={() => setLegalEntityData(p => ({ ...p, isSecuritiesRegulated: true }))}
                              />
                              <span>Yes</span>
                            </label>
                            <label className="radio">
                              <input
                                type="radio"
                                name="isSecuritiesRegulated"
                                checked={legalEntityData.isSecuritiesRegulated === false}
                                onChange={() => setLegalEntityData(p => ({ ...p, isSecuritiesRegulated: false }))}
                              />
                              <span>No</span>
                            </label>
                          </div>
                          {errors.isSecuritiesRegulated && <span role="alert">{errors.isSecuritiesRegulated}</span>}
                        </div>

                        {/* Consents */}
                        <div id="note_confirm_div" style={{ marginTop: '24px' }}>
                          {/* 1. Information Accuracy */}
                          <div
                            className={`form-group--checkbox ${informationAccuracy ? 'checked' : ''}`}
                            style={{
                              backgroundColor: showErrors && !informationAccuracy ? '#ffebee' : undefined,
                              borderLeft: showErrors && !informationAccuracy ? '4px solid #be1717' : undefined
                            }}
                          >
                            <label htmlFor="informationAccuracy">
                              <input
                                type="checkbox"
                                id="informationAccuracy"
                                checked={informationAccuracy}
                                onChange={(e) => setInformationAccuracy(e.target.checked)}
                              />
                              <span>
                                I hereby confirm that the information provided is accurate, correct and complete <span className="star-color">*</span>
                              </span>
                            </label>
                          </div>
                          {showErrors && !informationAccuracy && (
                            <div style={{ color: '#be1717', fontSize: '11.5px', marginTop: '-8px', marginBottom: '12px', paddingLeft: '4px', fontWeight: '500' }}>
                              <i className="bi bi-exclamation-circle-fill me-1"></i> Please confirm that the information provided is accurate.
                            </div>
                          )}

                          {/* 2. Terms and Conditions */}
                          <div
                            className={`form-group--checkbox ${termsAccepted ? 'checked' : ''}`}
                            onClick={handleTermsCheckboxClick}
                            style={{
                              backgroundColor: showErrors && !termsAccepted ? '#ffebee' : undefined,
                              borderLeft: showErrors && !termsAccepted ? '4px solid #be1717' : undefined
                            }}
                          >
                            <label htmlFor="termsCheckbox">
                              <input
                                type="checkbox"
                                id="termsCheckbox"
                                checked={termsAccepted}
                                readOnly={!termsAccepted}
                                onChange={(e) => {
                                  if (termsAccepted) {
                                    setTermsAccepted(e.target.checked);
                                  }
                                }}
                              />
                              <span>
                                I have read, understood and hereby accept the{' '}
                                <a
                                  href="javascript:void(0);"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    openTermsModal();
                                  }}
                                >
                                  Terms and Conditions
                                </a>{' '}
                                <span className="star-color">*</span>
                              </span>
                            </label>
                          </div>
                          {showErrors && !termsAccepted && (
                            <div style={{ color: '#be1717', fontSize: '11.5px', marginTop: '-8px', marginBottom: '12px', paddingLeft: '4px', fontWeight: '500' }}>
                              <i className="bi bi-exclamation-circle-fill me-1"></i> Please accept the Terms and Conditions to continue.
                            </div>
                          )}
                        </div>

                        {/* Submit Button */}
                        <div id="button_div" style={{ marginTop: '20px' }}>
                          <div className="row">
                            <div className="col-md-6">
                              <div className="single-field mb-0">
                                <button
                                  className="btn btn-primary"
                                  type="submit"
                                  disabled={loading}
                                >
                                  {loading ? 'Submitting...' : 'Submit'}
                                </button>
                              </div>
                            </div>
                            <div className="col-md-6">
                              <div className="single-field mb-0">
                                <button
                                  type="button"
                                  className="btn btn-outline-primary"
                                  onClick={() => navigate('/investor/register/email', { state: { email, registerAs, consentGiven } })}
                                >
                                  Back
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      </form>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Right Panel: Creative Illustration Panel */}
            <div className="right-panel">
              <RegistrationVisualCard step="details" registerAs={registerAs} />
            </div>
          </div>
        </div>
      </section>

      {/* Modals */}
      <TermsModal
        show={showTermsModal}
        onClose={closeTermsModal}
        onAccept={acceptTerms}
      />
      <CompactFooter />
    </>
  );
};

export default SelfRegistrationStep3;
